import React, { useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Palette, Pencil, Check, ImageOff, Link2 } from 'lucide-react';
import { extractError } from '@/lib/errors';
import { useDesignConfig } from '@/hooks/useDesignConfig';
import DesignPicker from '@/components/designs/DesignPicker';

/**
 * OrderDesignSection — Order Detail header widget for the Design Master link.
 * Renders nothing when the company hasn't opted in AND the order has no design.
 *
 * Shows the linked design (thumbnail + RD number + "Linked to Design Master"
 * badge, click to preview the full image). Editors get a pencil/link button
 * that opens a dialog to either pick an EXISTING design or create a NEW one
 * from one of the order's own uploaded images.
 *
 * Props:
 *   order      order document (id, images, images_thumb, design_id, design_no,
 *              design_thumb, design_image)
 *   companyId  active company id
 *   canEdit    boolean — may link/change (non-sales editors)
 *   onUpdated  () => void — reload callback
 */
export function OrderDesignSection({ order, companyId, canEdit, onUpdated }) {
  const { enabled, refresh } = useDesignConfig(companyId);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('existing');
  const [designId, setDesignId] = useState(null);
  const [imgIdx, setImgIdx] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  if (!enabled && !order?.design_id) return null;

  const hasDesign = !!order?.design_id;
  const images = order?.images || [];
  const thumbs = order?.images_thumb || [];
  const hasUsableImage = images.some((s) => typeof s === 'string' && s.startsWith('data:image'));

  const openDialog = () => {
    setMode('existing');
    setDesignId(null);
    setImgIdx(null);
    setOpen(true);
  };

  const submit = async () => {
    if (mode === 'existing' && !designId) { toast.error('Pick a design first'); return; }
    if (mode === 'new' && imgIdx == null) { toast.error('Pick one of the order images'); return; }
    setSaving(true);
    try {
      const payload = mode === 'existing'
        ? { order_id: order.id, mode: 'existing', design_id: designId }
        : { order_id: order.id, mode: 'new', source_image_idx: imgIdx };
      const { data } = await api.post('/designs/assign', payload);
      toast.success(`Linked to ${data.design_no}`);
      setOpen(false);
      refresh();
      onUpdated?.();
    } catch (e) {
      toast.error(extractError(e, 'Failed to link design'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <span className="inline-flex items-center gap-1" data-testid="order-detail-design">
        {hasDesign ? (
          <button
            type="button"
            onClick={() => setPreview(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-indigo-200 bg-indigo-50 pl-1 pr-2 py-0.5 hover:bg-indigo-100 transition-colors duration-150"
            title="View design image"
            data-testid="order-detail-design-badge"
          >
            {(order.design_thumb || order.design_image) ? (
              <img src={order.design_thumb || order.design_image} alt={order.design_no} className="w-5 h-5 rounded object-cover" />
            ) : (
              <Palette size={11} className="text-indigo-600" />
            )}
            <span className="font-mono text-[11px] text-indigo-700">{order.design_no}</span>
            <span className="text-[10px] text-indigo-500 hidden sm:inline">· Linked to Design Master</span>
          </button>
        ) : (
          <Badge variant="outline" className="bg-slate-50 text-slate-500 border-slate-200 font-mono text-[11px]">
            <Palette size={11} className="mr-0.5" /> No design
          </Badge>
        )}
        {canEdit && (
          <button
            type="button"
            onClick={openDialog}
            className="inline-flex items-center rounded p-1 text-slate-400 hover:text-[var(--ink-navy)] hover:bg-slate-100 transition-colors duration-150"
            title={hasDesign ? 'Change design' : 'Link a design'}
            data-testid="order-detail-design-edit-button"
          >
            {hasDesign ? <Pencil size={12} /> : <Link2 size={12} />}
          </button>
        )}
      </span>

      {/* Preview full image */}
      <Dialog open={preview} onOpenChange={setPreview}>
        <DialogContent className="max-w-lg" data-testid="order-detail-design-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-mono text-indigo-700">{order.design_no}</DialogTitle>
            <DialogDescription>Master design image linked to this order.</DialogDescription>
          </DialogHeader>
          {(order.design_image || order.design_thumb) ? (
            <img
              src={order.design_image || order.design_thumb}
              alt={order.design_no}
              className="w-full max-h-[60vh] object-contain rounded-md border border-[var(--divider)] bg-slate-50"
              data-testid="order-detail-design-preview-image"
            />
          ) : (
            <div className="py-10 text-center text-slate-400"><ImageOff size={30} className="mx-auto" /></div>
          )}
        </DialogContent>
      </Dialog>

      {/* Link / change dialog */}
      <Dialog open={open} onOpenChange={(o) => { if (!saving) setOpen(o); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="order-detail-design-dialog">
          <DialogHeader>
            <DialogTitle>{hasDesign ? 'Change Design' : 'Link a Design'}</DialogTitle>
            <DialogDescription>
              Attach this order to an existing design, or create a new design from one of its uploaded images.
            </DialogDescription>
          </DialogHeader>
          <div className="inline-flex rounded-md border border-indigo-200 bg-white overflow-hidden mb-3">
            <button
              type="button"
              onClick={() => setMode('existing')}
              className={`px-3 py-1 text-[12px] transition-colors duration-150 ${mode === 'existing' ? 'bg-indigo-600 text-white' : 'text-indigo-700 hover:bg-indigo-50'}`}
              data-testid="order-detail-design-mode-existing"
            >
              Existing design
            </button>
            <button
              type="button"
              onClick={() => setMode('new')}
              disabled={!hasUsableImage}
              className={`px-3 py-1 text-[12px] transition-colors duration-150 ${mode === 'new' ? 'bg-indigo-600 text-white' : 'text-indigo-700 hover:bg-indigo-50'} ${!hasUsableImage ? 'opacity-40 cursor-not-allowed' : ''}`}
              data-testid="order-detail-design-mode-new"
            >
              New from image
            </button>
          </div>
          {mode === 'existing' ? (
            <DesignPicker companyId={companyId} value={designId} onChange={(id) => setDesignId(id)} testIdPrefix="order-detail-design-picker" />
          ) : hasUsableImage ? (
            <div>
              <div className="text-[12px] text-slate-600 mb-2">Choose which uploaded image becomes the new design&apos;s Master image:</div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {images.map((src, i) => {
                  const isImage = typeof src === 'string' && src.startsWith('data:image');
                  if (!isImage) return null;
                  const t = thumbs[i] || src;
                  const sel = imgIdx === i;
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setImgIdx(i)}
                      className={`relative rounded-lg border overflow-hidden aspect-square bg-slate-50 ${sel ? 'border-indigo-500 ring-2 ring-indigo-300' : 'border-[var(--divider)] hover:border-indigo-300'}`}
                      data-testid={`order-detail-design-image-${i}`}
                    >
                      <img src={t} alt="" className="w-full h-full object-cover" />
                      {sel && <span className="absolute top-1 right-1 bg-indigo-600 text-white rounded-full p-0.5"><Check size={12} /></span>}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-[12px] text-slate-500 bg-slate-50 border border-[var(--divider)] rounded-md px-3 py-2">
              This order has no uploaded images to build a design from. Pick an existing design instead, or upload an image first.
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button
              onClick={submit}
              className="luxury-button"
              disabled={saving || (mode === 'existing' ? !designId : imgIdx == null)}
              data-testid="order-detail-design-save"
            >
              {saving ? 'Saving…' : 'Link Design'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default OrderDesignSection;
